import { Box, Flex } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useCallback } from 'react';

import { envConfig } from '#constants/envConfig';
import { openAppSettings } from '#features/notifications/utils/deviceSettings';
import { Button, MicOffIcon } from '#features/ui';

import { useMicDenialHint } from './useMicDenialHint';

type MicDenialNoteProps = {
  /** Разрешение выдали прямо сейчас — предупреждение пора убирать. */
  onResolved?: () => void;
};

/**
 * Предупреждение «микрофон запрещён» ДО входа в разговор — с кнопкой, а не только с советом.
 *
 * <p>До этого человек узнавал о запрете, уже сидя в комнате, из строки мелким шрифтом, и совет
 * там был один на все среды: «разрешите доступ в браузере» — в приложении, где браузера нет,
 * это прямая неправда. Прод за 30 дней: 32 сеанса с отказом микрофона, половина всех звонков из
 * Android-браузера; в четырёх подряд звонках одного человека 24.08 его просто не слышали, и он
 * ушёл.
 *
 * <p>Подпись, а не блокировка: вход остаётся доступен всегда. Ответу браузера про разрешения
 * верить в сторону «разрешено» нельзя (см. {@link queryMicPermission}), и запретить вход по
 * такому ответу значило бы иногда не пускать человека с рабочим микрофоном.
 */
export const MicDenialNote = ({ onResolved }: MicDenialNoteProps) => {
  const { t } = useLingui();
  const hint = useMicDenialHint();

  /**
   * В приложении ведём в системные настройки одним тапом: путь из четырёх шагов, набранный
   * текстом, не проходит никто. В вебе такого экрана нет — там кнопка предлагает повторить
   * захват, потому что снятый в настройках сайта запрет Chrome подхватывает без перезагрузки,
   * а человек об этом не знает и уходит перезагружать страницу.
   */
  const openSettings = useCallback(() => {
    void openAppSettings();
  }, []);

  const retry = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Трек нужен был ровно на то, чтобы проверить разрешение: оставленный включённым, он
      // зажигает индикатор микрофона в системе у человека, который ещё никуда не вошёл.
      stream.getTracks().forEach((track) => track.stop());
      onResolved?.();
    } catch {
      // Всё ещё запрещено — подпись остаётся на месте, и это честный ответ на нажатие.
    }
  }, [onResolved]);

  return (
    <Flex
      data-testid="mic-denial-note"
      align="center"
      gap={2}
      px={4}
      py={2.5}
      /*
       * Предупреждение, а не ошибка, и красным тут быть нельзя. Красный в звонке занят сбросом:
       * на экране вызова эта подпись стоит прямо над кнопкой «Отклонить», и красная строка над
       * красной кнопкой читается как часть кнопки. По той же причине оранжевый у значка «нет
       * доступа», и такой же — у подсказки в активном звонке; теперь семейство сошлось.
       */
      color="fg.warning"
      textStyle="xs"
    >
      <Flex align="center" flex="none">
        <MicOffIcon fontSize="14px" />
      </Flex>
      <Box as="span" minW={0} flex={1} lineHeight={1.35}>
        {hint}
      </Box>
      {envConfig.IS_NATIVE ? (
        <Button size="xs" variant="outline" flex="none" onClick={openSettings}>
          {t`Open settings`}
        </Button>
      ) : (
        <Button size="xs" variant="outline" flex="none" onClick={() => void retry()}>
          {t`Try again`}
        </Button>
      )}
    </Flex>
  );
};
